import {
  deliveryZoneInputSchema,
  type DeliveryZoneDto,
  type DeliveryZoneInput,
  type ZoneGeometry,
} from "@school-sales-ai/shared";
import { centsToPesos, pesosToCents } from "@/lib/format-mxn";

/**
 * The zone editor's pure half: what the form holds, and how it becomes the body
 * the route accepts. The SHARED schema is the judge — the browser and the server
 * refuse exactly the same zones — and this only adds the two messages the schema
 * cannot phrase for an admin: a fee typed in pesos, and a map nobody drew on.
 */

export type ZoneFormValues = {
  name: string;
  /** Pesos as typed. Required — a zone always quotes a fee; `0` is free delivery. */
  fee: string;
  geometry: ZoneGeometry | null;
};

export type ZoneFormField = "name" | "fee" | "geometry";

export function emptyZoneForm(): ZoneFormValues {
  return { name: "", fee: "", geometry: null };
}

export function zoneToForm(zone: DeliveryZoneDto): ZoneFormValues {
  return { name: zone.name, fee: centsToPesos(zone.feeCents), geometry: zone.geometry };
}

export function toZoneInput(
  values: ZoneFormValues,
): { ok: true; input: DeliveryZoneInput } | { ok: false; errors: Map<ZoneFormField, string> } {
  const errors = new Map<ZoneFormField, string>();

  const feeText = values.fee.trim();
  const feeCents = feeText === "" ? null : pesosToCents(feeText);
  if (feeText === "") {
    errors.set("fee", "Escribe el costo de envío de esta zona (0 si es gratis)");
  } else if (feeCents === null) {
    errors.set("fee", "Ingresa un monto válido en pesos, por ejemplo 45 o 45.50");
  }
  if (!values.geometry || !hasRings(values.geometry)) {
    errors.set("geometry", "Dibuja la zona en el mapa");
  }

  const parsed = deliveryZoneInputSchema.safeParse({
    name: values.name,
    feeCents: feeCents ?? 0,
    geometry: values.geometry,
  });
  if (!parsed.success) {
    for (const issue of parsed.error.issues) {
      const field = issue.path[0];
      if ((field === "name" || field === "fee" || field === "geometry") && !errors.has(field)) {
        errors.set(field, issue.message);
      }
      if (field === "feeCents" && !errors.has("fee")) errors.set("fee", issue.message);
    }
  }

  if (errors.size > 0 || !parsed.success) return { ok: false, errors };
  return { ok: true, input: parsed.data };
}

function hasRings(geometry: ZoneGeometry): boolean {
  return geometry.type === "Polygon"
    ? geometry.coordinates.length > 0
    : geometry.coordinates.some((p) => p.length > 0);
}

/**
 * Several drawn shapes → one zone. A zone may be in pieces (two neighbourhoods
 * the driver covers, separated by one he does not), so the drawings combine into
 * a MultiPolygon; a single one stays a Polygon. Null when nothing is drawn.
 */
export function combinePolygons(parts: readonly ZoneGeometry[]): ZoneGeometry | null {
  const polygons = parts.flatMap((g) =>
    g.type === "Polygon" ? [g.coordinates] : g.coordinates,
  );
  if (polygons.length === 0) return null;
  const [only] = polygons;
  if (polygons.length === 1 && only) return { type: "Polygon", coordinates: only };
  return { type: "MultiPolygon", coordinates: polygons };
}

/**
 * One colour per zone, the same on the map and in the list. The `dot` classes
 * are literals on purpose: Tailwind's JIT only sees class names that appear
 * whole in the source.
 */
export const ZONE_COLORS = [
  { stroke: "#0284c7", dot: "bg-sky-600" },
  { stroke: "#d97706", dot: "bg-amber-600" },
  { stroke: "#7c3aed", dot: "bg-violet-600" },
  { stroke: "#059669", dot: "bg-emerald-600" },
  { stroke: "#db2777", dot: "bg-pink-600" },
  { stroke: "#4b5563", dot: "bg-gray-600" },
  { stroke: "#dc2626", dot: "bg-red-600" },
  { stroke: "#0d9488", dot: "bg-teal-600" },
] as const;

export function zoneColor(index: number): (typeof ZONE_COLORS)[number] {
  return ZONE_COLORS[((index % ZONE_COLORS.length) + ZONE_COLORS.length) % ZONE_COLORS.length] ??
    ZONE_COLORS[0];
}
